import React, { useState, useEffect, useCallback, useRef } from 'react';
import { expensesService } from '../services/api';
import { monthNames } from '../utils/formatters';
import { campo, useEstadoDaCarga, EstadoDaLista } from '../design';
import { CartaoDoTopo, contornoDoSinal } from './shared/lista/PecasDaLista';
import { FRASE_DA_COMPETENCIA } from '../utils/fluxoDeCaixa';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtPct = (v: number) => v.toFixed(2).replace('.', ',') + '%';

const fmtMonthLabel = (m: string) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return `${(monthNames[parseInt(mo) - 1] || '').slice(0, 3)}/${y}`;
};

/**
 * O PADRÃO DA CENTRAL NO ANALÍTICO (23/10/2026): superfície neutra e valor em branco. RECEITAS em verde e DESPESAS em
 * vermelho, linha a linha, eram decoração (o bloco já diz o que é); o resultado leva a barra pelo sinal (verde no
 * positivo, vermelha no negativo). A cor de cada categoria é a CADASTRADA (identidade): fica na bolinha e na barra (K2,
 * decisão do Heitor).
 */
const CARTAO = 'bg-central-cartao rounded-central border border-central-borda';
const VALOR = 'text-central-texto tabular-nums whitespace-nowrap';

export default function AnalyticalFlowView() {
  const [startMonth, setStartMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [endMonth, setEndMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandRevenue, setExpandRevenue] = useState(false);

  // D4 (11/10/2026): a carga calava e não levava o período. Uma troca de período que falhava deixava a tabela do período
  // ANTERIOR sob os seletores novos, e o Excel saía com os dados velhos e o NOME do período novo. Agora cada leitura leva o
  // período; a tela só mostra (e só exporta) o que é do período dos seletores.
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const chave = `${startMonth}|${endMonth}`;
  const chavePedida = useRef(chave);
  chavePedida.current = chave;
  const [chaveDosDados, setChaveDosDados] = useState<string | null>(null);
  const dadosDoPeriodo = chaveDosDados === chave;
  const loadData = useCallback(async () => {
    setLoading(true);
    const pedida = `${startMonth}|${endMonth}`;
    await rodarCarga(async () => {
      const result = await expensesService.getAnalyticalFlow(startMonth, endMonth);
      if (chavePedida.current !== pedida) return;
      setData(result);
      setChaveDosDados(pedida);
    });
    setLoading(false);
  }, [startMonth, endMonth, rodarCarga]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleCategory = (name: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleExportExcel = () => {
    if (!data || !dadosDoPeriodo) return;
    const rows: string[][] = [];

    rows.push(['Fluxo Analitico', `Periodo: ${fmtMonthLabel(startMonth)} a ${fmtMonthLabel(endMonth)}`]);
    rows.push([]);

    // Revenue
    rows.push(['RECEITAS', `R$ ${fmt(data.totalRevenue)}`, '100,00%']);
    for (const client of data.revenueByClient || []) {
      rows.push([`  ${client.name}`, `R$ ${fmt(client.total)}`, fmtPct(client.percent), `${client.count} fatura(s)`]);
    }
    rows.push([]);

    // Expenses
    rows.push(['DESPESAS', `R$ ${fmt(data.totalExpenses)}`, fmtPct(data.totalRevenue > 0 ? (data.totalExpenses / data.totalRevenue) * 100 : 0)]);
    for (const cat of data.expenseCategories || []) {
      rows.push([`  ${cat.name}`, `R$ ${fmt(cat.total)}`, fmtPct(cat.percent)]);
      for (const detail of cat.details || []) {
        rows.push([`    ${detail.name}`, `R$ ${fmt(detail.total)}`, '', `${detail.count}x`]);
      }
    }
    rows.push([]);

    // Result
    rows.push(['RESULTADO', `R$ ${fmt(data.result)}`, fmtPct(data.totalRevenue > 0 ? (data.result / data.totalRevenue) * 100 : 0)]);

    const csv = rows.map(r => r.map(c => `"${c || ''}"`).join(';')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FluxoAnalitico_${startMonth}_${endMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalRevenue = data?.totalRevenue || 0;
  const totalExpenses = data?.totalExpenses || 0;
  const result = data?.result || 0;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">De:</span>
            <input
              type="month"
              value={startMonth}
              onChange={e => setStartMonth(e.target.value)}
              className={`rounded-lg px-3 py-1.5 text-sm [color-scheme:dark] ${campo}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Ate:</span>
            <input
              type="month"
              value={endMonth}
              onChange={e => setEndMonth(e.target.value)}
              className={`rounded-lg px-3 py-1.5 text-sm [color-scheme:dark] ${campo}`}
            />
          </div>
          <button onClick={loadData} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-sm">Atualizar</button>
        </div>
        <button onClick={handleExportExcel} disabled={!data || !dadosDoPeriodo} title={!dadosDoPeriodo ? 'A tabela não é do período selecionado: o Excel sairia com o período errado no cabeçalho.' : undefined} className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm flex items-center gap-1.5 disabled:opacity-50">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
          Excel
        </button>
      </div>

      {/* D5: o "Fluxo Analitico" mora sob Fluxo de Caixa, mas é faturado, não recebido. */}
      <p className="text-xs text-slate-400" data-testid="regime-do-analitico">{FRASE_DA_COMPETENCIA}</p>

      {!dadosDoPeriodo ? (
        <EstadoDaLista carga={{ ...carga, estado: carga.estado === 'erro' ? 'erro' : 'carregando', atualizadoEm: null }} temDados={false} oQue="o fluxo analítico do período selecionado" onTentarDeNovo={loadData} vazio={null}>
          {null}
        </EstadoDaLista>
      ) : !data ? (
        <div className="text-center text-slate-500 py-12">Nenhum dado disponivel</div>
      ) : (
        <>
          {/* Os cartões do topo (padrão das listas): valor branco; o resultado leva a barra pelo sinal. */}
          <div className="grid grid-cols-3 gap-4">
            <CartaoDoTopo rotulo="Total de receitas" valor={`R$ ${fmt(totalRevenue)}`} />
            <CartaoDoTopo rotulo="Total de despesas" valor={`R$ ${fmt(totalExpenses)}`}
              sub={`${totalRevenue > 0 ? fmtPct((totalExpenses / totalRevenue) * 100) : '—'} do faturamento`} />
            <CartaoDoTopo rotulo="Resultado" valor={`R$ ${fmt(result)}`} className={contornoDoSinal(result)}
              sub={`Margem: ${totalRevenue > 0 ? fmtPct((result / totalRevenue) * 100) : '—'}`} />
          </div>

          {/* RECEITAS */}
          <div className={`${CARTAO} overflow-hidden`}>
            <button
              onClick={() => setExpandRevenue(!expandRevenue)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-central-elevado transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{expandRevenue ? '▼' : '▶'}</span>
                <span className="text-central-texto font-bold">RECEITAS</span>
                <span className="text-slate-500 text-xs">({(data.revenueByClient || []).length} clientes)</span>
              </div>
              <div className="flex items-center gap-4">
                <span className={`${VALOR} font-bold`}>R$ {fmt(totalRevenue)}</span>
                <span className="text-central-texto-fraco text-sm">100,00%</span>
              </div>
            </button>
            {expandRevenue && (
              <div className="border-t border-central-borda">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-700/30">
                    {(data.revenueByClient || []).map((client: any, i: number) => (
                      <tr key={i} className="hover:bg-slate-700/20">
                        <td className="px-4 py-2 text-slate-300 pl-8">{client.name}</td>
                        <td className={`px-4 py-2 text-right w-[140px] ${VALOR}`}>R$ {fmt(client.total)}</td>
                        <td className="px-4 py-2 text-right text-slate-500 w-[80px] text-xs">{fmtPct(client.percent)}</td>
                        <td className="px-4 py-2 text-right text-slate-600 w-[80px] text-xs">{client.count} fat.</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* DESPESAS by category */}
          <div className={`${CARTAO} overflow-hidden`}>
            <div className="px-4 py-3 border-b border-central-borda flex items-center justify-between">
              <span className="text-central-texto font-bold">DESPESAS</span>
              <div className="flex items-center gap-4">
                <span className={`${VALOR} font-bold`}>R$ {fmt(totalExpenses)}</span>
                <span className="text-central-texto-fraco text-sm">
                  {totalRevenue > 0 ? fmtPct((totalExpenses / totalRevenue) * 100) : '—'}
                </span>
              </div>
            </div>
            <div className="divide-y divide-slate-700/30">
              {(data.expenseCategories || []).map((cat: any) => {
                const isExpanded = expandedCategories.has(cat.name);
                // Progress bar width relative to largest category
                const maxCat = Math.max(...(data.expenseCategories || []).map((c: any) => c.total));
                const barWidth = maxCat > 0 ? (cat.total / maxCat) * 100 : 0;

                return (
                  <div key={cat.name}>
                    <button
                      onClick={() => toggleCategory(cat.name)}
                      className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-central-elevado transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-xs text-slate-500">{isExpanded ? '▼' : '▶'}</span>
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                        <span className="text-slate-200 text-sm font-medium truncate">{cat.name}</span>
                        <div className="flex-1 mx-3 h-1.5 bg-slate-700 rounded-full overflow-hidden hidden md:block">
                          <div className="h-full rounded-full" style={{ width: `${barWidth}%`, backgroundColor: cat.color }} />
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className={`${VALOR} font-medium`}>R$ {fmt(cat.total)}</span>
                        <span className="text-slate-500 text-xs w-[60px] text-right">{fmtPct(cat.percent)}</span>
                      </div>
                    </button>
                    {isExpanded && cat.details?.length > 0 && (
                      <div className="bg-slate-900/30 border-t border-slate-700/50">
                        <table className="w-full text-xs">
                          <tbody className="divide-y divide-slate-700/20">
                            {cat.details.map((d: any, i: number) => (
                              <tr key={i} className="hover:bg-slate-700/10">
                                <td className="px-4 py-1.5 text-slate-400 pl-12">{d.name}</td>
                                <td className="px-4 py-1.5 text-right text-slate-300 w-[140px]">R$ {fmt(d.total)}</td>
                                <td className="px-4 py-1.5 text-right text-slate-600 w-[80px]">
                                  {totalRevenue > 0 ? fmtPct((d.total / totalRevenue) * 100) : ''}
                                </td>
                                <td className="px-4 py-1.5 text-right text-slate-600 w-[60px]">{d.count}x</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RESULTADO: valor branco; a barra pelo sinal (estado é barra, não fundo verde ou vermelho). */}
          <div className={`${CARTAO} p-5 ${contornoDoSinal(result)}`} data-resultado={result < 0 ? 'negativo' : 'positivo'}>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-central-texto">RESULTADO OPERACIONAL</span>
              <div className="flex items-center gap-4">
                <span className={`text-2xl font-bold ${VALOR}`}>R$ {fmt(result)}</span>
                <span className="text-sm text-central-texto-fraco">
                  {totalRevenue > 0 ? fmtPct((result / totalRevenue) * 100) : '—'}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
